import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog'
import { Experience } from '@/types'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { CldImage } from 'next-cloudinary'
import { useState, useRef, useEffect } from 'react'
import CroppedCloudinaryImage from './cropped-cloudinary-image'

export function ExperienceMediaDialog({
  experience,
  open,
  onOpenChange,
  initialIndex = 0,
}: {
  experience: Experience
  open?: boolean
  onOpenChange?: (open: boolean) => void
  initialIndex?: number
}) {
  const images = experience.images || []
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [touchStart, setTouchStart] = useState<number | null>(null)
  const [touchEnd, setTouchEnd] = useState<number | null>(null)
  const carouselRef = useRef<HTMLDivElement>(null)

  const minSwipeDistance = 50

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex)
    }
  }, [open, initialIndex])

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1
    )
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === images.length - 1 ? 0 : prevIndex + 1
    )
  }
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null)
    setTouchStart(e.targetTouches[0].clientX)
  }

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX)
  }

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return

    const distance = touchStart - touchEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && images.length > 1) {
      goToNext()
    }
    if (isRightSwipe && images.length > 1) {
      goToPrevious()
    }
  }

  const [mouseStart, setMouseStart] = useState<number | null>(null)
  const [mouseEnd, setMouseEnd] = useState<number | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const onMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    setMouseEnd(null)
    setMouseStart(e.clientX)
  }

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return
    setMouseEnd(e.clientX)
  }

  const onMouseUp = () => {
    if (!isDragging) return
    setIsDragging(false)

    if (!mouseStart || !mouseEnd) return

    const distance = mouseStart - mouseEnd
    const isLeftSwipe = distance > minSwipeDistance
    const isRightSwipe = distance < -minSwipeDistance

    if (isLeftSwipe && images.length > 1) {
      goToNext()
    }
    if (isRightSwipe && images.length > 1) {
      goToPrevious()
    }
  }

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (images.length <= 1) return

      if (e.key === 'ArrowLeft') {
        setCurrentIndex((prevIndex) =>
          prevIndex === 0 ? images.length - 1 : prevIndex - 1
        )
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prevIndex) =>
          prevIndex === images.length - 1 ? 0 : prevIndex + 1
        )
      } else if (e.key === 'Escape' && open) {
        onOpenChange?.(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [images.length, open, onOpenChange])

  if (images.length === 0) {
    return null
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open === undefined && <DialogTrigger asChild></DialogTrigger>}
      <DialogContent
        className="max-w-screen w-screen h-full p-0 bg-white [&>button]:hidden"
        onPointerDownOutside={() => onOpenChange?.(false)}
      >
        <div className="flex flex-col h-full">
          <button
            onClick={() => onOpenChange?.(false)}
            className="absolute bottom-12 md:bottom-4 right-1/2 translate-x-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors duration-200 z-20"
            aria-label="Close"
          >
            <X size={20} />
          </button>

          <div
            ref={carouselRef}
            className="flex-1 relative overflow-hidden select-none"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={() => setIsDragging(false)}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            <div className="flex h-[calc(100vh-200px)] transition-transform duration-300 ease-in-out">
              {images.map((image, index) => (
                <div
                  key={index}
                  className="w-full h-full flex-shrink-0 flex items-center justify-center  mt-[100px]"
                  style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                >
                  <CroppedCloudinaryImage
                    src={image}
                    alt={`Experience image ${index + 1}`}
                    fill
                    className="max-w-full max-h-full object-contain pointer-events-none"
                  />
                </div>
              ))}
            </div>

            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors duration-200 z-10"
                  aria-label="Previous image"
                >
                  <ChevronLeft />
                </button>

                <button
                  onClick={goToNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-colors duration-200 z-10"
                  aria-label="Next image"
                >
                  <ChevronRight />
                </button>
              </>
            )}

            {images.length > 1 && (
              <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded-full text-sm z-10">
                {currentIndex + 1} / {images.length}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
